import {
    ReactElement,
    useState,
    useCallback,
    useEffect,
    useRef,
    useMemo
} from 'react';
import styles from './onboarding-team.module.scss';
import { REGION_BRAND_LOGOS } from '@/common/assets/brands';
import {
    UIButton,
    UIFormInput,
    UIMultiSelectInput,
    UISelectValue,
    UISpacer,
    UIText,
    UITextButton
} from '@/ui';
import { Trans } from 'react-i18next';
import { useCompaniesTranslation } from '../hooks';
import isEqual from 'lodash/isEqual';
import {
    TEAM_NAME_MAX_LENGTH,
    validateEmail,
    validateTeamName
} from '@/common/util';
import { useRegion } from '@/common/providers/region-provider';

type User = {
    email: string;
    emailError: string;
    admin: boolean;
    guardian: boolean;
    roles: UISelectValue[];
    rolesError: string;
};

type OnboardingTeamProps = {
    userEmail: string;
    initialTeamName: string;
    initialUsers: {
        email: string;
        admin: boolean;
        guardian: boolean;
        roles: UISelectValue[];
    }[];
    loading: boolean;
    onChange: (data: {
        teamName: string;
        users: {
            email: string;
            admin: boolean;
            guardian: boolean;
            roles: UISelectValue[];
        }[];
    }) => void;
    onContinue: () => void;
    onBack: () => void;
};

export function OnboardingTeam(props: OnboardingTeamProps): ReactElement {
    const {
        userEmail,
        initialTeamName,
        initialUsers,
        loading,
        onChange,
        onContinue,
        onBack
    } = props;

    const { t } = useCompaniesTranslation();
    const { resolvedRegion } = useRegion();

    const bottomOfListDivRef = useRef<HTMLDivElement>(null);

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Team Name
     */
    const [teamName, setTeamName] = useState(initialTeamName);
    const [teamNameError, setTeamNameError] = useState('');
    const checkTeamName = useCallback(
        (teamName: string): string => {
            const error = validateTeamName(teamName);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        setTeamNameError(checkTeamName(teamName));
    }, [teamName, checkTeamName, onChange]);

    const checkEmail = useCallback(
        (__email: string, allEmails: string[]): string => {
            const email = __email.trim().toLowerCase();

            // Allow empty emails
            if (email === '') {
                return '';
            }

            const error = validateEmail(email);
            if (error) {
                return t(error);
            }

            // Is email same as owners email
            if (email === userEmail.toLowerCase().trim()) {
                return t('shared.fields.invite_email.error_same_as_user');
            }

            // Is email same as another email already listed
            if (
                allEmails.filter((item) => item.trim().toLowerCase() === email)
                    .length > 1
            ) {
                return t('shared.fields.invite_email.error_already_listed');
            }

            return '';
        },
        [t, userEmail]
    );

    const checkRoles = useCallback(
        (roles: UISelectValue[], email: string): string => {
            if (email.trim().length === 0) {
                return '';
            }
            if (roles.length === 0) {
                return t('shared.fields.team_roles.error_empty');
            }
            return '';
        },
        [t]
    );

    const [users, setUsers] = useState<User[]>(() => {
        const users: User[] = [];

        const allEmails = initialUsers.map((user) =>
            user.email.trim().toLowerCase()
        );
        for (const initialUser of initialUsers) {
            users.push({
                email: initialUser.email,
                emailError: checkEmail(initialUser.email, allEmails),
                admin: initialUser.admin,
                guardian: initialUser.guardian,
                roles: initialUser.roles,
                rolesError: checkRoles(initialUser.roles, initialUser.email)
            });
        }

        return users;
    });

    useEffect(() => {
        // Every time the users count changes, go to the bottom of the list
        bottomOfListDivRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [users.length]);

    const hasError = useMemo(() => {
        return (
            !!teamNameError ||
            users.filter((user) => user.emailError || user.rolesError).length >
                0
        );
    }, [teamNameError, users]);

    const allEmails = useMemo(() => {
        return users.map((user) => user.email.trim().toLowerCase());
    }, [users]);

    useEffect(() => {
        const newUsers = [
            ...users.map((user) => ({
                ...user,
                emailError: checkEmail(user.email, allEmails),
                rolesError: checkRoles(user.roles, user.email)
            }))
        ];

        if (!isEqual(users, newUsers)) {
            setUsers(newUsers);
        }
    }, [teamNameError, users, allEmails, checkEmail, checkRoles, onChange]);

    /**
     * On change
     */
    useEffect(() => {
        onChange({
            teamName,
            users
        });
    }, [teamName, users, onChange]);

    return (
        <div className={styles.container}>
            <div className={styles.firstHalf}>
                <div className={styles.firstHalfContent}>
                    <UIText variant="h1" bold>
                        {t('components.onboarding_team.title')}
                    </UIText>
                    <UISpacer multiplier={1} />
                    <UIText type="default2">
                        <Trans
                            ns="companies"
                            components={{
                                b: <UIText medium type="default" />
                            }}
                        >
                            components.onboarding_team.message
                        </Trans>
                    </UIText>

                    <UISpacer multiplier={2} />

                    <UIFormInput
                        name="teamName"
                        label={t('shared.fields.team_name.label')}
                        value={teamName}
                        placeholder={t('shared.fields.team_name.placeholder')}
                        autoFocus={true}
                        type="text"
                        errorText={showRealTimeErrors ? teamNameError : ''}
                        required
                        maxLength={TEAM_NAME_MAX_LENGTH}
                        onChange={setTeamName}
                    />

                    <UISpacer multiplier={2} />

                    <div className={styles.staffContainer}>
                        <div className={styles.headerRow}>
                            <div className={styles.headerItem}>
                                <UIText>
                                    {t(
                                        'components.onboarding_team.staff_email'
                                    )}
                                </UIText>
                            </div>
                            <div className={styles.headerItem}>
                                <UIText>
                                    {t('components.onboarding_team.team_roles')}
                                </UIText>
                            </div>
                        </div>
                        {users.map((item, index) => (
                            <UserRow
                                key={index}
                                user={item}
                                allEmails={allEmails}
                                onChange={(user) => {
                                    const newUsers = [...users];
                                    newUsers[index] = user;
                                    setUsers(newUsers);
                                }}
                                showRealTimeErrors={showRealTimeErrors}
                                checkEmail={checkEmail}
                                checkRoles={checkRoles}
                            />
                        ))}
                        <div ref={bottomOfListDivRef} />
                    </div>

                    {users.length < 50 && (
                        <div>
                            <UISpacer />

                            <UITextButton
                                variant="primary"
                                onClick={() => {
                                    const newUsers = [...users];
                                    newUsers.push({
                                        email: '',
                                        emailError: '',
                                        admin: false,
                                        guardian: false,
                                        roles: [],
                                        rolesError: ''
                                    });
                                    setUsers(newUsers);
                                }}
                            >
                                {t('components.onboarding_team.add_more')}
                            </UITextButton>
                        </div>
                    )}

                    <UISpacer multiplier={2} />

                    <div className={styles.buttonContainer}>
                        <UIButton
                            variant={'primary'}
                            loading={loading}
                            disabled={loading}
                            onClick={() => {
                                if (hasError) {
                                    setShowRealTimeErrors(true);
                                } else {
                                    onContinue();
                                }
                            }}
                        >
                            {t('shared.labels.finish')}
                        </UIButton>
                        <UISpacer multiplier={2} />
                        <UITextButton onClick={onBack}>
                            {t('shared.labels.back')}
                        </UITextButton>
                    </div>
                </div>
            </div>
            <div className={styles.secondHalf}>
                <div className={styles.secondHalfContent}>
                    <UIText variant="h1" textAlign="center" type="link">
                        {t('components.onboarding_team.trusted_by')}
                    </UIText>
                    <div className={styles.logos}>
                        {REGION_BRAND_LOGOS[resolvedRegion].map(
                            (row, rowIndex) => (
                                <div key={rowIndex} className={styles.logoRow}>
                                    {row.map((logo) => (
                                        <div
                                            key={logo.brand}
                                            className={styles.logoContainer}
                                        >
                                            <img
                                                src={logo.src}
                                                alt={t(
                                                    'components.onboarding_team.logo_alt',
                                                    {
                                                        brand: logo.brand
                                                    }
                                                )}
                                                className={styles.logoImage}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

type UserRowProps = {
    user: User;
    allEmails: string[];
    onChange: (user: User) => void;
    showRealTimeErrors: boolean;
    checkEmail: (email: string, allEmails: string[]) => string;
    checkRoles: (roles: UISelectValue[], email: string) => string;
};
function UserRow(props: UserRowProps): ReactElement {
    const {
        user,
        allEmails,
        onChange,
        showRealTimeErrors,
        checkEmail,
        checkRoles
    } = props;

    const { t } = useCompaniesTranslation();
    const admin: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.admin.name'),
            subLabel: t('shared.roles.team.admin.description'),
            value: 'admin'
        }),
        [t]
    );
    const guardian: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.guardian.name'),
            subLabel: t('shared.roles.team.guardian.description'),
            value: 'guardian'
        }),
        [t]
    );
    const memberOnly: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.member.name'),
            subLabel: t('shared.roles.team.member.description'),
            value: 'member'
        }),
        [t]
    );

    const teamRolesOptions: UISelectValue[] = useMemo(() => {
        // If roles contains member, only show member
        if (user.roles.some((role) => role.value === 'member')) {
            return [memberOnly];
        } else if (user.roles.length > 0) {
            return [admin, guardian];
        } else {
            return [admin, guardian, memberOnly];
        }
    }, [user.roles, admin, guardian, memberOnly]);

    return (
        <div className={styles.itemRow}>
            <UIFormInput
                value={user.email}
                errorText={showRealTimeErrors ? user.emailError : ''}
                onChange={(value) => {
                    onChange({
                        ...user,
                        email: value,
                        emailError: checkEmail(value, allEmails)
                    });
                }}
                style={{ flex: 1 }}
            />
            <UIMultiSelectInput
                placeholder=""
                required
                options={teamRolesOptions}
                value={user.roles}
                style={{ flex: 1 }}
                onChange={(value) => {
                    onChange({
                        ...user,
                        roles: value,
                        rolesError: checkRoles(value, user.email)
                    });
                }}
                errorText={user.rolesError}
            />
        </div>
    );
}
